import React, { useEffect, useState } from "react";
import { Button, Select, Space, Typography, message } from "antd";
import { MailOutlined, SaveOutlined } from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { ReceiptEmailService } from "../services/receiptEmailService";

const { Text, Title } = Typography;

const MAX_RECEIPT_EMAILS = 10;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ReceiptEmailManagerProps {
  organizationId: string;
  title?: string;
}

export const ReceiptEmailManager: React.FC<ReceiptEmailManagerProps> = ({
  organizationId,
  title = "Payment Receipt Emails",
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [emails, setEmails] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!organizationId) return;

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const result = await ReceiptEmailService.getReceiptEmails(
          organizationId,
          token
        );
        if (!cancelled) {
          setEmails(result.emails || []);
          setDirty(false);
        }
      } catch (error) {
        console.error("Error loading receipt emails:", error);
        if (!cancelled) {
          message.error("Failed to load receipt email addresses");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [organizationId, getAccessTokenSilently]);

  const handleChange = (values: string[]) => {
    const normalized = values
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean);
    setEmails(normalized);
    setDirty(true);
  };

  const handleSave = async () => {
    const invalid = emails.find((email) => !EMAIL_PATTERN.test(email));
    if (invalid) {
      message.error(`Invalid email address: ${invalid}`);
      return;
    }
    if (emails.length > MAX_RECEIPT_EMAILS) {
      message.error(`At most ${MAX_RECEIPT_EMAILS} receipt emails are allowed`);
      return;
    }

    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const result = await ReceiptEmailService.updateReceiptEmails(
        organizationId,
        emails,
        token
      );
      setEmails(result.emails || []);
      setDirty(false);
      message.success("Receipt email addresses saved");
    } catch (error: any) {
      console.error("Error saving receipt emails:", error);
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        "Failed to save receipt email addresses";
      message.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Title level={5}>
        <MailOutlined /> {title}
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        These addresses receive a receipt email whenever a payment is received.
        Add one or more emails, then save.
      </Text>
      <Select
        mode="tags"
        style={{ width: "100%" }}
        placeholder="Type an email and press Enter"
        value={emails}
        onChange={handleChange}
        tokenSeparators={[",", " "]}
        disabled={loading || saving || !organizationId}
        open={false}
        aria-label="Receipt email addresses"
      />
      <Space style={{ marginTop: 8 }}>
        <Button
          type="primary"
          size="small"
          icon={<SaveOutlined />}
          loading={saving}
          disabled={!dirty || loading || !organizationId}
          onClick={handleSave}
        >
          Save receipt emails
        </Button>
        <Text type="secondary">
          {emails.length}/{MAX_RECEIPT_EMAILS} addresses
        </Text>
      </Space>
    </div>
  );
};
